import styled from "@emotion/styled";
import classNames from "classnames";
import { useRef, type MouseEventHandler } from "react";

import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { IconButton } from "../buttons";
import { Icon, SkiliticsIcon } from "../icons";
import { zIndexes } from "../utilities";
import { ImportToast } from "./ImportToast";
import { useImportToaster } from "./ImportToasterContext";

const Container = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;

  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  width: 425px;
  background-color: ${({ theme }) => theme.surface};

  border-radius: 5px;
  box-shadow: 0 4px 6px 0 rgba(0, 0, 0, 0.25);

  &.complete,
  &.postponed {
    opacity: 0;
    pointer-events: none;
  }

  &.postponed {
    transition: opacity 250ms ease-in-out 2000ms;
  }

  z-index: ${zIndexes.importToaster};

  transition: opacity 250ms ease-in-out 300ms;
`;

const Header = styled.div`
  display: flex;
  align-items: center;

  height: 50px;

  padding: 10px 25px 5px 25px;
  border-start-start-radius: 5px;
  border-start-end-radius: 5px;

  background-color: ${({ theme }) => theme.surfaceHighlight};

  font-size: ${({ theme }) => theme.fontSizeSmall};
`;

const Tray = styled.div`
  display: flex;
  align-items: center;

  height: 54px;
  width: 100%;

  color: ${({ theme }) => theme.warning};

  font-size: ${({ theme }) => theme.fontSize};

  border-radius: 5px;

  padding-inline: 15px;
`;

const TrayToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;

  height: 100%;
  width: 100%;

  cursor: pointer;

  user-select: none;

  &.complete,
  &.postponed {
    cursor: default;
    pointer-events: none;
  }

  &.complete {
    color: ${({ theme }) => theme.tertiary};
  }

  &.postponed {
    color: ${({ theme }) => theme.secondary};
  }

  transition: color 150ms ease-in-out;
`;

const Message = styled.span``;

const Caret = styled.div`
  margin-inline-start: auto;
`;

const Separator = styled.div`
  height: 40px;
  width: 1px;

  border-inline-end: 1px solid ${({ theme }) => theme.surfaceSoft};

  margin-inline: 15px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-inline-start: auto;

  height: 100%;

  color: ${({ theme }) => theme.generalTextIcon};
`;

const ToastContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  max-height: 40dvh;
  background-color: ${({ theme }) => theme.surfaceHighlight};

  padding: 10px;

  overflow-y: auto;
`;

export const ImportToaster = () => {
  const formatMessage = useFormatMessage(formatStrings);

  const containerRef = useRef<HTMLDivElement>(null);

  const { state, trayState, toasts, dismissToast, hide, closeTray, toggleTray } = useImportToaster();

  const handleClose: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation();
    closeTray();
    hide();
  };

  const complete = toasts.length === 0;
  const postponed = state === "hidden" && toasts.length > 0;

  const text = postponed ? formatMessage("postponed") : formatMessage(complete ? "complete" : "message");

  const handleToggle: MouseEventHandler<HTMLDivElement> = (event) => {
    event.stopPropagation();
    toggleTray();
  };

  return (
    <Container ref={containerRef} className={classNames({ complete: state === "hidden", postponed })}>
      {trayState === "open" && !complete && (
        <>
          <Header>
            <span>{formatMessage("header")}</span>
          </Header>
          <ToastContainer>
            {toasts.map((toast) => (
              <ImportToast key={toast.id} {...toast} onDismiss={() => dismissToast(toast.id)} />
            ))}
          </ToastContainer>
        </>
      )}
      <Tray data-test="import-toaster-tray">
        <TrayToggle
          className={classNames({ complete, postponed })}
          onClick={handleToggle}
          data-test="import-toaster-tray-toggle"
        >
          <SkiliticsIcon icon={postponed ? "info" : complete ? "tick" : "warning-2"} size={20} />
          <Message>{text}</Message>
          {!(complete || postponed) && (
            <Caret>
              <Icon icon={trayState === "open" ? "angle-down" : "angle-up"} />
            </Caret>
          )}
        </TrayToggle>
        <ButtonContainer className={classNames({ complete, postponed })}>
          <Separator />
          <IconButton icon="close" onClick={handleClose} data-test="close-import-toaster-button" />
        </ButtonContainer>
      </Tray>
    </Container>
  );
};

const formatStrings = {
  header: {
    en: "The item pasted relies on elements not accessible to this activity. Follow the solutions below and tick off what you resolve.",
  },
  message: {
    en: "Unable to paste due to missing references.",
  },
  complete: {
    en: "All issues marked as resolved.",
  },
  postponed: {
    en: "Resolution postponed.",
  },
} satisfies FormatMessages;
